import {
  getAllPodcastsSocialShareStats,
  AllPodcastsSocialStatsResponse,
  AllPodcastsSocialStatsArgs,
} from 'api/services';
import { useCallback } from 'react';
import { UseQueryOptions, useQuery } from 'react-query';
import { embedVideoQueries } from './queryKeys';
import {
  allPodcastsSocialInfoSelector,
  allPodcastsStatsSummarySelector,
  hasAllPodcastsStatsSelector,
  podcastTitleSelector,
} from './selectors';

type QueryKey = ReturnType<typeof embedVideoQueries.podcasts>;

function useGetAllPodcastsSocialShareStats<
  TData = AllPodcastsSocialStatsResponse,
>(
  args?: AllPodcastsSocialStatsArgs,
  opts?: Omit<
    UseQueryOptions<AllPodcastsSocialStatsResponse, unknown, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<AllPodcastsSocialStatsResponse, unknown, TData, QueryKey>(
    embedVideoQueries.podcasts(args),
    ({ queryKey: [{ platform }] }) =>
      getAllPodcastsSocialShareStats({ platform }),
    opts,
  );
}

export function useAllPodcastsSocialShareInfo() {
  return useGetAllPodcastsSocialShareStats(
    {},
    {
      select: allPodcastsSocialInfoSelector,
    },
  );
}

export function useHasAllPodcastsSocialShareInfo(requestStatus?: boolean) {
  return useGetAllPodcastsSocialShareStats(
    {},
    {
      enabled: requestStatus,
      select: hasAllPodcastsStatsSelector,
    },
  );
}

export function useAllPodcastSocialStatsSummary() {
  return useGetAllPodcastsSocialShareStats(
    {
      platform: [
        'facebook',
        'instagram',
        'threads',
        'twitter',
        'linkedin',
        'tiktok',
      ],
    },
    { select: allPodcastsStatsSummarySelector },
  );
}

export function useAllPodcastsYoutubeStatsSummary() {
  return useGetAllPodcastsSocialShareStats(
    { platform: ['youtube'] },
    { select: allPodcastsStatsSummarySelector },
  );
}

export function useGetPodcastTitleFromSocialStatsInfo(
  podcastId: string | undefined,
) {
  const selector = useCallback(
    (d: AllPodcastsSocialStatsResponse) => podcastTitleSelector(d, podcastId),
    [podcastId],
  );
  return useGetAllPodcastsSocialShareStats(
    {},
    {
      select: selector,
    },
  );
}
